import { Box, Color, Space, ZIndex } from '@gousto-internal/citrus-react'
import { Recipe } from '@library/menu-service'
import React, { RefObject } from 'react'
import { useFetchRafCampaignDetails } from '../hooks/useFetchRafCampaignDetails'
import {
  AboveTheFoldSection,
  BrandEndorsementsSection,
  CustomerReviewsSection,
  DiscountBanner,
  DiscountPerWeek,
  EndlessVarietySection,
  HeroTitle,
  HowItWorksSection,
  OurSuppliersSection,
  ValueYouCanTasteSection,
} from '@library/landing-pages'
import type { DiscountBox, HeroSlidesConfig } from '@library/landing-pages'
import { redirectToWizard } from '../util/redirectToWizard'
import { heroGetStartedTrackingEvent } from '../tracking/trackingConfig'
export type RafLandingPageType = {
  onScrollHandler: ({
    heroRef,
    stickyCTARef,
    setIsSticky,
    setMaxHeight,
  }: {
    heroRef: RefObject<HTMLDivElement>
    stickyCTARef: RefObject<HTMLDivElement>
    setIsSticky: (isSticky: boolean) => void
    setMaxHeight: (isSticky: number) => void
  }) => void
  onSignupCTAClick: () => void
  recipes: Recipe[]
  signupCTA: string
}

export const RafLandingPage = ({ onSignupCTAClick, recipes, signupCTA }: RafLandingPageType) => {
  const { firstBoxDiscount, firstMonthDiscount, secondBoxDiscount } = useFetchRafCampaignDetails()
  const isThreeStepHeroEnabled = !!secondBoxDiscount

  const rafHeroSlides: HeroSlidesConfig = {
    titlePart1: 'Your friend',
    titlePart2: 'must like',
    titlePart3: 'you a lot!',
    titlePart4: '',
    subTitlePart1Mobile: 'Over a million people have',
    subTitlePart2Mobile: 'signed up for Gousto thanks',
    subTitlePart3Mobile: 'to their mates',
    subTitlePart1Desktop: 'Over a million people have signed up',
    subTitlePart2Desktop: 'for Gousto thanks to their mates',
  }

  const rafDiscountBoxes: DiscountBox[] = isThreeStepHeroEnabled
    ? [
        {
          discountValue: `${firstBoxDiscount}%`,
          discountSuffix: 'OFF',
          label: '1st box',
          bgColor: Color.Primary_600,
          textColor: Color.White,
        },
        {
          discountValue: `${secondBoxDiscount}%`,
          discountSuffix: 'OFF',
          label: '2nd box',
          bgColor: Color.ColdGrey_500,
          textColor: Color.White,
        },
        {
          discountValue: `${firstMonthDiscount}%`,
          discountSuffix: 'OFF',
          label: '3rd-8th box',
          bgColor: Color.ColdGrey_500,
          textColor: Color.White,
        },
      ]
    : [
        {
          discountValue: `${firstBoxDiscount}%`,
          discountSuffix: 'OFF',
          label: '1st box',
          bgColor: Color.Primary_600,
          textColor: Color.White,
        },
        {
          discountValue: `${firstMonthDiscount}%`,
          discountSuffix: 'OFF',
          label: '2nd-8th box',
          bgColor: Color.ColdGrey_500,
          textColor: Color.White,
        },
      ]

  return (
    <Box paddingBottom={[10, 6]} bg={Color.White}>
      <AboveTheFoldSection
        onCTAClick={() => redirectToWizard(heroGetStartedTrackingEvent())}
        signupCTA={signupCTA}
        heroSlides={rafHeroSlides}
        heroTitleBox={
          <Box width={['348px', null, null, '460px']} zIndex={ZIndex.Mask}>
            <Space size={[14, null, 18]} />
            <HeroTitle slides={rafHeroSlides} suffix={null} />
          </Box>
        }
        discountBanner={
          <DiscountBanner onBannerCTAClick={onSignupCTAClick} title="Your referral offer">
            <DiscountPerWeek boxes={rafDiscountBoxes} />
          </DiscountBanner>
        }
      />
      <CustomerReviewsSection />
      <HowItWorksSection signupCTA={signupCTA} onCTAClick={onSignupCTAClick} />
      <ValueYouCanTasteSection signupCTA={signupCTA} onCTAClick={onSignupCTAClick} />
      <EndlessVarietySection
        signupCTA={signupCTA}
        onCTAClick={onSignupCTAClick}
        recipes={recipes}
      />
      <BrandEndorsementsSection />
      <OurSuppliersSection />
    </Box>
  )
}
